import * as _ from "lodash";
import * as THREE from "three";
import {MaterialJson} from "./metadata/MaterialJson";
import {Node} from "./Node";
import {PNodeList} from "./properties/PNodeList";
import {json, MaterialFactory, MetadataRow} from "../global";
import Commons from "oc-modules/oc-commons/Commons";
import {MaterialImport} from "../ui/components/MaterialImport";
import {Model} from "./metadata/Model";
import {SceneExplorer} from "../ui/components/SceneExplorer";
import {EnvMapJson} from "./metadata/EnvMapJson";
import {v3} from "oc-threejs/short3";
import {PSubNode} from "./properties/PSubNode";
import {GenericMetaNode, GenericMetaNodeConstructor} from "./metadata/GenericMetaNode";
import {EnvironmentJson} from "./metadata/EnvironmentJson";
import {LightJson} from "./metadata/LightJson";
import {NodeMaterialFactory} from "./NodeMaterialFactory";
import {PNodeListMeta} from "./properties/PNodeListMeta";
import {ThreeMixinInterface} from "./metadata/ThreeMixin";
import UniversalLoader from "oc-threejs/loader/UniversalLoader";

export class SceneNode extends Node {

    metadataNodeClasses = Commons.slurpDirectory(require.context('./metadata/', true));
    metaToNodeMap = new WeakMap<object, GenericMetaNode>();
    forceUiUpdate: (...args) => {};
    triggers = new Set<string>();
    materials: MaterialFactory;
    nodesThatNeedUpdateScene = new Set<ThreeMixinInterface<any>>();
    globalChangeCounter = 0;

    // lights3d = new Set<LightJson>();
    // materials3d = new Set<MaterialJson>();
    // Model: Model[];
    // Light: LightJson[];
    // Material: MaterialJson[];
    // EnvMaps: EnvMapJson[];
    // environmentJson: EnvironmentJson;

    createProperties() {
        const createNodeListMeta = (name, visible = true) => new PNodeListMeta({
            "name": name,
            "label": null,
            node: this,
            showInEditor: (p) => visible,
        });
        // @ts-ignore
        return [
            {
                "group": "Model",
            },
            createNodeListMeta('Model'),
        ].concat(
            APP.metadata.ModelMaterialRelation
                ? [
                    {
                        "group": "ModelMaterialRelation",
                    },
                    createNodeListMeta('ModelMaterialRelation'),
                ]
                : []
        ).concat([
            {
                "group": "Material",
            },
            createNodeListMeta('MaterialJson'),
            {
                "button": I18N("scene editor:import materials"),
                "click": ({node, setSelected}) => setSelected({
                    getName: () => "Materialien importieren",
                    render: MaterialImport,
                    props: (node, setSelected) => ({node, setSelected, nodeIndex: 0}),
                })
            },
            {
                "group": "Light",
            },
            createNodeListMeta('LightJson'),
            {
                "group": "EnvMap",
            },
            createNodeListMeta('EnvMapJson', false),
            {
                "group": "Environment"
            },
            new PSubNode({
                "name": "environmentNode",
                label: null,
                node: this.environmentJson,
            }),
            {
                "group": "Camera"
            },
            new PSubNode({
                "name": "cameraNode",
                label: null,
                node: this.environmentJson,
            }),
            {
                "group": "Renderer"
            },
            new PSubNode({
                "name": "rendererNode",
                label: null,
                node: this.environmentJson,
            }),
            {
                "group": "Explorer",
            },
            {
                render: SceneExplorer,
                props: (node, setSelected) => ({
                    axisLabeled: false,
                    scene: APP.scene,
                    sceneNode: this,
                    setSelected
                })
            }
        ]);
    }

    get environmentJson(): EnvironmentJson {
        const metaTable = APP.richMetadata.getMetaTable('EnvironmentJson');
        const environmentJson = APP.metadata.EnvironmentJson.find(x => x.name.toLowerCase() == 'default')
            ?? APP.metadata.EnvironmentJson[0]
            ?? {};
        return <EnvironmentJson>this.getMetaNodeLazy(environmentJson, metaTable);
    }

    callDebouncedUpdateSceneOn(...nodes: Node[]) {
        nodes.filter(node => node['updateScene']).forEach(node => this.nodesThatNeedUpdateScene.add(node as any));
        this._updateSceneDebounced();
    }

    _inUpdate(inner: () => void) {
        this.triggers.clear();
        inner();
        this.forceUiUpdate && this.forceUiUpdate({});
        this.triggers.forEach(t => APP.trigger(t));
        STAGE.dirty();
    }

    _updateSceneDebounced = _.debounce(() => {
        this._inUpdate(() => {
            this.nodesThatNeedUpdateScene.forEach(node => {
                // console.log('call updateScene on ',node);
                node.updateScene();
            });
            this.nodesThatNeedUpdateScene.clear();
        });
    }, 100);

    init() {
        super.init();
        this._inUpdate(() => {
            ['EnvMapJson', 'LightJson', 'MaterialJson', 'ModelMaterialRelation']
                .forEach(name => {
                    const property = this.findProperty(name);
                    if (property) {
                        const nodes = this.findProperty(name).get();
                        nodes.forEach(node => {
                            // console.log('call updateScene on ',node);
                            node.init();
                        });
                    }
                });
            this.environmentJson.init();
            this.nodesThatNeedUpdateScene.clear();
        });
        return this;
    }

    loadModelPromise(modelName: string) {
        if (Commons.isEmpty(modelName)) {
            return Promise.resolve(null);
        }
        const modelData = APP.metadata['Model'].find(model => model.Key == modelName);
        if (modelData == null) {
            throw new Error('unknown model ' + modelName);
        }
        return APP.modelsHelper.loadModelPromise(modelData);
    }

    getEnvMapNode(name) {
        if (Commons.isEmpty(name)) {
            return null;
        }
        const envMapList = this.findProperty('EnvMapJson') as PNodeList<EnvMapJson>;
        return envMapList.findKey(name);
    }

    getTextureUrl(path) {
        if (path == null) {
            return null;
        }
        return (/^(http|https|file):\/\/.*$/.test(path))
            ? path
            : APP.sceneImporter.texture_root + path;
    }

    getMaterial(name) {
        const materialJson = this.findProperty('MaterialJson') as PNodeList<MaterialJson>;
        return materialJson.findKey(name)?.threeValue;
    }

    getName() {
        return "Scene";
    }

    getMetaTable(name) {
        return APP.metamodel["tables"].find(metaTable => metaTable.name == name);
    }

    getTableName() {
        return 'EnvironmentJson';
    }

    createMaterialsFactory() {
        return new NodeMaterialFactory(APP.sceneImporter.texture_root, this);
    }

    getMaterialJsonProperty = Commons.cacheIdentity(() => this.findProperty('MaterialJson') as PNodeList<MaterialJson>);

    findMaterialJson(name) {
        return this.getMaterialJsonProperty().findKey(name);
    }

    getTextureRoot() {
        return APP.sceneImporter.texture_root;
    }

    getPreviewModelPromise(material3d: THREE.Material): Promise<THREE.Object3D> {
        if (material3d instanceof THREE.LineBasicMaterial) {
            const line = new THREE.LineSegments(
                new THREE.BufferGeometry().setFromPoints([v3(-1, 0, 0), v3(+1, 0, 0)]),
                material3d
            );
            return Promise.resolve(line);
        }

        // const modelList = this.findProperty('Model') as PNodeList<GenericModelNode>;
        // const mapIncludesMaterial = mmr => mmr['MaterialMap'] && _.values(mmr['MaterialMap']).includes(material3d.name);
        // let modelMaterialMap: { model: GenericModelNode, MaterialMap: KeyValue };
        // if (this.metadata['ModelMaterialRelation']) {
        //     const mmr = this.metadata['ModelMaterialRelation'].find(mapIncludesMaterial);
        //     if (mmr) {
        //         const model = modelList.findKey(mmr.Model.Key);
        //         if (model) {
        //             modelMaterialMap = {model, MaterialMap: mmr['MaterialMap']};
        //         }
        //     }
        // } else {
        //     const model = modelList.get().find(mapIncludesMaterial);
        //     if (model) {
        //         modelMaterialMap = {model, MaterialMap: model['MaterialMap']};
        //     }
        // }
        // if (modelMaterialMap) {
        //     const model3dProperty = modelMaterialMap.model.findProperty('Path') as PModel3d;
        //     const url = model3dProperty.getUriEncoded() + '?' + Math.random();
        //     const materialFactory = new ReplacingMaterialFactory(modelMaterialMap.MaterialMap, UniversalLoader.getMaterialFactory());
        //     return UniversalLoader.loadModelPromise(url, materialFactory).then(object3d => {
        //         this.modelsHelper.applyToObject3d(modelMaterialMap.model, object3d);
        //         return object3d;
        //     });
        // }

        const materialDummy3d = new THREE.Group();
        const box = new THREE.Mesh(new THREE.BoxBufferGeometry(1, 1, 1), material3d);
        box.castShadow = true;
        box.receiveShadow = true;
        box.position.y = 1;
        materialDummy3d.add(box);

        const sphere = new THREE.Mesh(new THREE.SphereBufferGeometry(0.5), material3d);
        sphere.castShadow = true;
        sphere.receiveShadow = true;
        sphere.position.y = 2;
        materialDummy3d.add(sphere);

        return Promise.resolve(materialDummy3d);
    }

    getMetaNodeLazy(meta: MetadataRow, metaTable: json): GenericMetaNode {
        let node = this.metaToNodeMap.get(meta);
        if (node == null) {
            node = this.createMetaNode(meta, metaTable);
            this.metaToNodeMap.set(meta, node);
            node.init();
        }
        return node;
    }

    createMetaNode(meta: MetadataRow, metaTable: json): GenericMetaNode {
        const nodeClass = this.getNodeClassForMetaTable(metaTable);
        return new nodeClass(metaTable, meta)
    }

    getNodeClassForMetaTable(metaTable): GenericMetaNodeConstructor {
        return this.metadataNodeClasses[metaTable.name] ?? GenericMetaNode;
    }

    saveToServer() {
        this.environmentJson.saveToServer();
    }

    resetScene() {
        this.globalChangeCounter++;
        Commons.resetCaches();
        APP.materials = this.createMaterialsFactory();
        UniversalLoader.setMaterialFactory(APP.materials);
        APP.arrangement = APP.arrangement.clone();
        APP.dirty();
    }

    // importMetadata() {
    // this.importJsonTable(this.findProperty('EnvMaps'), 'EnvMapJson');
    // this.importJsonTable(this.findProperty('Light'), 'LightJson');
    // this.importMetatable(this.findProperty('Model'), 'Model');
    // this.importJsonTable(this.findProperty('Material'), 'MaterialJson');
    // this.importEnvironmentMetadata();
    // const contentUrl = this.settings["content_url"].ensureRight('/');
    // const nodesToSave = new Set();
    // const iterate = (n)=> {
    //     n.getProperties().forEach(p=>{
    //         if (p instanceof AbstractProperty) {
    //             if (p instanceof AbstractUploadProperty) {
    //                 if (p.get() && p.get().startsWith(contentUrl)) {
    //                     p.set(p.get().substringAfter(contentUrl));
    //                     nodesToSave.add(p.node);
    //                 }
    //             }
    //             if (p instanceof PNodeList) {
    //                 p.get().forEach(n=>iterate(n));
    //             }
    //         }
    //     });
    // }
    // iterate(this);
    // nodesToSave.forEach(n=>n.saveToServer());

    // this.importOldMaterial();

    // if (this.metadata.Environment) {
    //     const metadata = this.metadata.Environment.find(e=>e.Standard);
    //     if (metadata) {
    //     }
    // }
    // }

    // importJsonTable(nodeListProperty, metatableName) {
    //     const metaData = APP.metadata[metatableName] ?? [];
    //     metaData.forEach(row => {
    //         const node = nodeListProperty.createInstance(this).fromJsonTableRow(row);
    //         nodeListProperty.add(node);
    //     });
    // }

    // importEnvironmentMetadata() {
    //     const metaData = APP.metadata['EnvironmentJson'] ?? [];
    //     const row = metaData.find(row => row.name == 'Default') || _.first(metaData);
    //     if (row) {
    //         this.fromJsonTableRow(row);
    //     }
    // }
    //
    // importMetatable(nodeListProperty, metatableName) {
    //     const metaTable = APP.metamodel["tables"].find(metaTable => metaTable.name == metatableName);
    //     const metaData = APP.metadata[metatableName] ?? [];
    //     metaData.forEach(row => {
    //         const node = nodeListProperty.createInstance(this).fromMetadataRow(row);
    //         nodeListProperty.add(node);
    //     });
    // }

    // importOldMaterial() {
    //     console.time('MaterialPbr import');
    //     const materialPropertyList = this.findProperty('Material');
    //     const programmaticMaterials = new Materials();
    //     Object.keys(programmaticMaterials).filter(k=>!/^_.*|loader$/.test(k)).forEach(materialName=>{
    //         const material3d = programmaticMaterials.create(materialName);
    //         const node = new MaterialNode(this,material3d);
    //         node.saveToServer();
    //         materialPropertyList.add(node);
    //     });
    //     ['Material','MaterialPbr'].forEach(metatableName=>{
    //         const metaData = this.metadata[metatableName]??[];
    //         metaData.forEach(row=>{
    //             const material3d = this.sceneImporter.create(row.name);
    //             const node = new MaterialNode(this,material3d);
    //             node.saveToServer();
    //             materialPropertyList.add(node);
    //         });
    //     });
    //     console.timeEnd('MaterialPbr import');
    // }

}

